import { Link } from 'react-router-dom';
import { DocsLayout } from '../components/DocsLayout';

export function BillingPage() {
  return (
    <DocsLayout
      title="Billing &amp; invoices"
      updated="May 9, 2026"
      summary="How Rynora bills, who can manage the subscription, and what to expect at renewal or downgrade."
    >
      <h2>Stage A note (today)</h2>
      <p>
        Rynora is in pre-launch. Billing endpoints are wired up but Stripe is not yet enabled — you can sign up, invite
        your team, and use Rynora at the Free tier limits. We will email Owners and Admins at least 14 days before
        turning paid plans on.
      </p>

      <h2>Who can manage billing</h2>
      <p>
        Admins and Owners can change plans, update the payment method, and download invoices. Users (the regular role)
        cannot see the billing page. See <Link to="/docs/user-roles">User roles</Link> for the full table.
      </p>

      <h2>Plans &amp; pricing</h2>
      <p>
        See <Link to="/docs/plans">Plans &amp; limits</Link> for the per-tier feature comparison. Once paid plans are
        live, prices appear on the <Link to="/">homepage</Link> and inside the in-app pricing page.
      </p>

      <h2>How billing works</h2>
      <ul>
        <li>
          <strong>Currency</strong> — USD by default. We can quote in GBP, EUR, AUD, or INR for Enterprise plans.
        </li>
        <li>
          <strong>Cycle</strong> — monthly or annual. Annual is billed up-front and saves ~17%.
        </li>
        <li>
          <strong>Proration</strong> — upgrading mid-cycle bills you the prorated difference immediately. Downgrading
          mid-cycle takes effect at the next renewal — you keep your current limits until then.
        </li>
        <li>
          <strong>Tax</strong> — Stripe handles VAT, GST, and US sales tax automatically based on your billing address.
          Add your VAT/GST number on the billing page to receive a reverse-charge invoice.
        </li>
        <li>
          <strong>Payment methods</strong> — credit/debit cards, ACH (US), SEPA (EU), and bank transfer for Enterprise.
        </li>
      </ul>

      <h2>Metered usage</h2>
      <p>
        Some line items (notably AI test generation and hosted runner minutes on Pro and above) are metered: you pay a
        flat monthly base plus per-unit overage. Usage rolls up to the same Stripe invoice. The current month's usage is
        visible on the billing page in real time.
      </p>
      <p>
        We email Admins when usage reaches 75% and 100% of the included allowance. Hard limits never exceed the included
        allowance unless you explicitly enable overage on the billing page.
      </p>

      <h2>Invoices &amp; receipts</h2>
      <p>
        Every renewal generates an invoice you can download as PDF from <strong>Settings → Billing → Invoices</strong>.
        We keep at least 7 years of history. Invoices are hosted on Stripe so they include the Rynora legal entity, VAT
        line, and payment status — your accounts team should be able to import them directly.
      </p>

      <h2>Failed payments</h2>
      <p>
        Stripe automatically retries failed cards on a smart schedule (4 attempts over 3 weeks). During that window the
        organisation keeps full access. If the final retry fails:
      </p>
      <ul>
        <li>
          The org is moved to a <strong>read-only grace</strong> state for 14 days.
        </li>
        <li>Admins receive a daily email with a one-click update-card link.</li>
        <li>After the grace period the org is downgraded to Free; existing data is preserved.</li>
      </ul>

      <h2>Cancelling</h2>
      <p>
        From <strong>Settings → Billing</strong> click <strong>Cancel plan</strong>. Your access remains at the paid
        tier through the end of the current billing period — you don't lose anything mid-month. After the period ends
        the org is downgraded to Free.
      </p>
      <p>
        We don't issue partial refunds for unused time except where required by law. If the cancellation is due to a bug
        or outage on our side, please email <a href="mailto:support@rynora.app">support@rynora.app</a> and we'll sort it
        out.
      </p>

      <h2>Deleting the organisation</h2>
      <p>
        Deleting the org from <strong>Settings → Danger zone</strong> cancels the subscription, schedules a 30-day
        anonymisation, and removes every member. Audit logs survive (anonymised). See{' '}
        <Link to="/docs/delete-and-restore">Delete &amp; restore</Link> for the equivalent project-level workflow.
      </p>

      <h2>Disputes &amp; refunds</h2>
      <p>
        For billing disputes, email <a href="mailto:billing@rynora.app">billing@rynora.app</a> with the invoice number
        and the line item you're disputing. We aim to respond within one business day. If you've already opened a
        chargeback with your card issuer, please tell us — we'd rather resolve it directly than fight it in Stripe's
        dispute UI.
      </p>

      <h2>Contact sales</h2>
      <p>
        Need a custom plan, multi-year contract, or invoice-based billing? Hit the{' '}
        <Link to="/contact">contact form</Link> with topic <em>Sales</em>.
      </p>
    </DocsLayout>
  );
}
